import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Upload, Select, message, Modal } from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { binaryContentsHooks as cHooks } from "../hooks";

const { Option } = Select;

const CreateContentPage = () => {
   const navigate = useNavigate(); 
  const [form] = Form.useForm();
  const [thumbnailBase64, setThumbnailBase64] = useState(null);
  const [backgroundBase64, setBackgroundBase64] = useState(null);
  const [groupList, setGroupList] = useState([]);
  const [authorList, setAuthorList] = useState([]);
  const [htmlContent, setHtmlContent] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState(""); // 이미지 URL 저장
  const [isPreview, setIsPreview] = useState(false);
  const quillRef = useRef(null);

  // 그룹 리스트 불러오기
  const authorGroups = async () => {
    try {
      const res = await cHooks.contentsAuthorList();
      if (res.data) setAuthorList(res.data.data);
    } catch (error) {
      message.error("작성자 리스트 불러오기 실패!");
    }
  };

  useEffect(() => {
    authorGroups()
  }, []);

  // Base64 변환 핸들러
  const handleBase64Upload = (file, setter) => {
    const reader = new FileReader();
    reader.onload = (e) => setter(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  // 이미지 업로드 후 URL 반환
  const handleImageUploadAndGetUrl = async (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Image = e.target.result.split(",")[1];
      try {
        const res = await cHooks.contentsImageUpload({ base64Image });
        if (res.data?.url) {
          setUploadedImageUrl(res.data.url);
          message.success("이미지 URL 반환 성공!");
        } else {
          throw new Error("URL 반환 실패");
        }
      } catch (error) {
        message.error("이미지 업로드 실패!");
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  // 이미지 URL 에디터 삽입
  const handleInsertImage = () => {
    if (uploadedImageUrl) {
      const quill = quillRef.current.getEditor();
      const currentPosition = quill.getSelection()?.index || quill.getLength();
      quill.insertEmbed(currentPosition, "image", uploadedImageUrl);
      setUploadedImageUrl("");
      message.success("이미지가 삽입되었습니다!");
    } else {
      message.error("유효한 이미지 URL이 없습니다!");
    }
  };

  // 폼 제출 처리
  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        content_thumbnail: thumbnailBase64.split(",")[1],
        content_background_image: backgroundBase64.split(",")[1],
        content_description: btoa(unescape(encodeURIComponent(htmlContent))),
      };

      console.log("📦 [2] 서버 전송 최종 Payload:", {
      contentTitle: payload.content_title,
      authorId: payload.author_id, // 이 값이 제대로 들어있는지 확인!
      contentSummary: payload.content_summary,
      thumbnailLength: payload.content_thumbnail?.length, 
      bgImageLength: payload.content_background_image?.length
    });
      const res = await cHooks.contentsCreate({
        contentTitle: payload.content_title,
        contentDescription: payload.content_description,
        contentBackgroundImage: payload.content_background_image,
        contentSummary: payload.content_summary,
        contentThumbnail: payload.content_thumbnail,
        contentType: "content",
        contentBinaryType: "text",
        contentVideoLink: "",
        authorId: values.author_id,
      });
      if (res.IsError) {
        throw new Error();
      }
      message.success("컨텐츠가 성공적으로 생성되었습니다!");
      form.resetFields();
      navigate("/contents");
    } catch (error) {
      message.error("컨텐츠 업로드 실패");
    }
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
      <h2>컨텐츠 생성 페이지</h2>
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Form.Item
          name="content_title"
          label="제목"
          rules={[{ required: true }]}
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        {/* 그룹 설정 */}
        {/* <Form.Item name="content_group_id" label="그룹 설정">
          <Select placeholder="그룹을 선택하세요">
            {groupList.map((group) => (
              <Option key={group.id} value={group.id}>
                {group.content_group_name}
              </Option>
            ))}
          </Select>
        </Form.Item> */}

        {/* 작성자 설정 */}
        <Form.Item 
          name="author_id" 
          label="작성자 설정"
          rules={[{ required: true, message: '작성자를 선택해주세요!' }]}
        >
          <Select 
            placeholder="작성자를 선택하세요"
            onChange={(value) => {
              // 선택된 author_id 값을 콘솔에 찍음
              const selectedAuthor = authorList.find(a => a.id === value);
              console.log("📍 작성자 선택됨:", {
                id: value,
                name: selectedAuthor?.user_name
              });
            }}
          >
            {authorList.map((author) => (
              <Option key={author.id} value={author.id}>
                {author.user_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* 태그 입력 */}
        {/* <Form.Item name="content_inner_tag" label="내부 태그">
          <Input placeholder="태그 입력 (예: 여성건강,수면)" />
        </Form.Item>
        <Form.Item name="content_tag" label="외부 태그">
          <Input placeholder="태그 입력 (예: 건강관리,수면질)" />
        </Form.Item>
        <Form.Item name="content_summary" label="컨텐츠 요약">
          <Input placeholder="컨텐츠 내용 요약" />
        </Form.Item> */}
        
        {/* 썸네일 업로드 */}
        <Form.Item label="썸네일">
          <Upload
            beforeUpload={(file) =>
              handleBase64Upload(file, setThumbnailBase64)
            }
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>썸네일 업로드</Button>
          </Upload>
          {thumbnailBase64 && (
            <img
              src={thumbnailBase64}
              alt="썸네일"
              style={{ width: "150px", marginTop: "10px" }}
            />
          )}
        </Form.Item>

        {/* 백그라운드 이미지 */}
        <Form.Item label="백그라운드 이미지">
          <Upload
            beforeUpload={(file) =>
              handleBase64Upload(file, setBackgroundBase64)
            }
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>백그라운드 이미지 업로드</Button>
          </Upload>
          {backgroundBase64 && (
            <img
              src={backgroundBase64}
              alt="배경 이미지"
              style={{ width: "150px", marginTop: "10px" }}
            />
          )}
        </Form.Item>

        {/* 이미지 URL 반환 */}
        <Form.Item label="이미지 업로드 및 URL 반환">
          <Upload
            beforeUpload={handleImageUploadAndGetUrl}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>이미지 업로드</Button>
          </Upload>
          <Input
            placeholder="반환된 이미지 URL"
            value={uploadedImageUrl}
            onChange={(e) => setUploadedImageUrl(e.target.value)}
            style={{ marginTop: "10px" }}
          />
          <Button
            type="dashed"
            onClick={handleInsertImage}
            disabled={!uploadedImageUrl}
            style={{ marginTop: "10px" }}
          >
            에디터에 이미지 삽입
          </Button>
        </Form.Item>

        {/* 요약 */}
        <Form.Item name="content_summary" label="컨텐츠 요약">
          <Input placeholder="컨텐츠 내용 요약" />
        </Form.Item>

        {/* 에디터 */}
        <Form.Item label="내용">
          <Button
            type="default"
            onClick={() => setIsPreview(!isPreview)}
            style={{ marginBottom: "10px" }}
          >
            {isPreview ? "에디터로 돌아가기" : "미리보기"}
          </Button>
          {!isPreview ? (
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={htmlContent}
              onChange={setHtmlContent}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, 4, 5, false] }],

                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
              style={{ height: "300px", marginBottom: "50px", width: "360px" }}
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{ border: "1px solid I'm Commingddd", padding: "10px" }}
            />
          )}
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            생성하기
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CreateContentPage;
